import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { Seo } from '../../../../components/seo.js';
import {
  StorefrontEmptyState,
  StorefrontErrorState,
  StorefrontLoadingState,
} from '../../components/states.js';
import { useCart, useCartMutations } from '../hooks/use-cart.js';
import { CartLineItem } from '../components/cart-line-item.js';
import { CartSummary } from '../components/cart-summary.js';
import { MobileStickySummary } from '../components/mobile-sticky-summary.js';
import { CouponInput } from '../components/coupon-input.js';
import { DeliveryChecker } from '../../delivery/components/delivery-checker.js';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';
import { formatPaise } from '../../../../lib/format.js';
import type { CartItem } from '../types/cart.js';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof StorefrontApiError) return error.message;
  return fallback;
}

export function CartPage() {
  const { data: cart, isLoading, isError, refetch } = useCart();
  const { update, remove } = useCartMutations();
  const [announcement, setAnnouncement] = useState('');
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'update' | 'remove' | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  /** Preview-only: the code that validated successfully against `/coupons/validate`, carried forward for checkout to actually apply -- this page never applies it itself. */
  const [previewedCouponCode, setPreviewedCouponCode] = useState<string | null>(null);

  const handleQuantityChange = (item: CartItem, quantity: number) => {
    setPendingItemId(item.id);
    setPendingAction('update');
    setErrorBanner(null);
    update.mutate(
      { itemId: item.id, input: { quantity } },
      {
        onSuccess: (view) => {
          setAnnouncement(`Quantity updated. Subtotal ${formatPaise(view.subtotalPaise)}`);
        },
        onError: (error) => setErrorBanner(errorMessage(error, 'Could not update the quantity. Please try again.')),
        onSettled: () => {
          setPendingItemId(null);
          setPendingAction(null);
        },
      },
    );
  };

  const handleRemove = (item: CartItem) => {
    setPendingItemId(item.id);
    setPendingAction('remove');
    setErrorBanner(null);
    remove.mutate(item.id, {
      onSuccess: (view) => {
        setAnnouncement(`Item removed. Subtotal ${formatPaise(view.subtotalPaise)}`);
      },
      onError: (error) => setErrorBanner(errorMessage(error, 'Could not remove this item. Please try again.')),
      onSettled: () => {
        setPendingItemId(null);
        setPendingAction(null);
      },
    });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 pb-28 sm:pb-8">
      <Seo title="My Cart" noindex />
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>

      <div className="mb-6 flex items-center gap-2">
        <ShoppingBag className="h-6 w-6 text-gray-400" aria-hidden="true" />
        <h1 className="text-xl font-bold text-gray-900">My Cart</h1>
      </div>

      {errorBanner && (
        <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {errorBanner}
        </div>
      )}

      {isLoading && <StorefrontLoadingState label="Loading your cart…" />}

      {isError && !isLoading && (
        <StorefrontErrorState message="Could not load your cart." onRetry={() => void refetch()} />
      )}

      {!isLoading && !isError && cart && cart.items.length === 0 && (
        <StorefrontEmptyState
          title="Your cart is empty"
          description="Add products to get started."
          action={
            <Link
              to={ROUTE_NAMES.shop}
              className="focus-visible:ring-brand rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2"
            >
              Browse products
            </Link>
          }
        />
      )}

      {!isLoading && !isError && cart && cart.items.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ul className="rounded-2xl border border-gray-100 bg-white px-4">
              {cart.items.map((item) => (
                <CartLineItem
                  key={item.id}
                  item={item}
                  onQuantityChange={handleQuantityChange}
                  onRemove={handleRemove}
                  isUpdating={pendingItemId === item.id && pendingAction === 'update'}
                  isRemoving={pendingItemId === item.id && pendingAction === 'remove'}
                />
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-4">
            <CartSummary cart={cart} />
            <CouponInput onValidCode={setPreviewedCouponCode} />
            {previewedCouponCode && (
              <p className="text-xs text-gray-400">
                “{previewedCouponCode}” will be applied at checkout.
              </p>
            )}
            <DeliveryChecker />
          </div>
        </div>
      )}

      {!isLoading && !isError && cart && cart.items.length > 0 && <MobileStickySummary cart={cart} />}
    </div>
  );
}
